import React, { memo, useEffect, useRef } from "react";

import { Image, ScrollView, StyleSheet, Text, View } from "react-native";
import type { MoneyFormat } from "@rista/core/utils/currency/format";

import {
  BillBreakdownRow,
  CustomerDisplaySummary,
  DEFAULT_ITEM_IMAGE_URL,
  getBillTotal,
  getCustomerDisplaySummary,
  getItemName,
  getOrderSummaryRows,
  ITEM_NOTE_ICON_URL,
  ItemDisplayInfo,
  OrderSectionProps,
} from "@rista/core/customerDisplay";

export const OrderSection = memo(function OrderSection({
  sale,
  items,
  money,
  itemDisplayInfo,
}: OrderSectionProps) {
  const summaryRows = getOrderSummaryRows(sale);

  const billTotal = getBillTotal(sale);

  const customerSummary = getCustomerDisplaySummary(sale);
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    scrollViewRef.current?.scrollToEnd({ animated: true });
  }, [items]);
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          Your Order 
        </Text>

        <CustomerChip summary={customerSummary} />
      </View>

      <View style={styles.columnHeaderRow}>
        <Text style={[styles.columnHeaderText, styles.columnItemsHeader]}>ITEMS</Text>

        <Text style={[styles.columnHeaderText, styles.columnQtyHeader]}>QTY</Text>

        <Text style={[styles.columnHeaderText, styles.columnPriceHeader]}>PRICE</Text>
      </View>

      <View style={styles.itemsWrapper}>
        <ScrollView
          ref={scrollViewRef}
          style={styles.items}
          contentContainerStyle={styles.itemsContent}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
          removeClippedSubviews
        >
          <ItemsRenderer items={items} money={money} itemDisplayInfo={itemDisplayInfo} />
        </ScrollView>
      </View>

      <OrderSummary money={money} summaryRows={summaryRows} billTotal={billTotal} />
    </View>
  );
});

// Props only, no useSelector: this renders in the desktop customer-display window, which is
// mounted outside the redux Provider (apps/web/src/index.tsx) — the points ride in on the
// pushed cart payload instead.
const CustomerChip = memo(function CustomerChip({
  summary,
}: {
  summary: CustomerDisplaySummary;
}) {
  return (
    <View style={styles.customerChip}>
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarGlyph}>{"\u{1F464}"}</Text>
        </View>

        {summary.isWalkIn ? (
          <Text style={styles.customerChipText}>{summary.channelName || "Walk-in Customer"}</Text>
        ) : (
          <View>
            {!!summary.name && <Text style={styles.customerName}>{summary.name}</Text>}

            {!!summary.maskedPhone && (
              <Text style={styles.customerPhone}>{summary.maskedPhone}</Text>
            )}
          </View>
      )}
    </View>
  );
});

const ItemsRenderer = memo(function ItemsRenderer({
  items,
  money,
  itemDisplayInfo,
}: {
  items: any[];
  money: MoneyFormat;
  itemDisplayInfo?: Record<string, ItemDisplayInfo>;
}) {
  return (
    <>
      {items.map((item, index) => (
        <OrderItem
          key={item?.itemId ? `${item.itemId}-${item?.itemNumber ?? index}` : index}
          item={item}
          money={money}
          displayInfo={item?.itemId ? itemDisplayInfo?.[item.itemId] : undefined}
        />
      ))}
    </>
  );
});

const OrderItem = memo(function OrderItem({
  item,
  money,
  displayInfo,
}: {
  item: any;
  money: MoneyFormat;
  displayInfo?: ItemDisplayInfo;
}) {
  const optionsText = item?.itemOptionList?.length
    ? item.itemOptionList.map((option: any) => `${option?.quantity} x ${option?.name}`).join(", ")
    : null;

  return (
    <View style={styles.item}>
      <View style={styles.itemMain}>
        {/* Image + veg/non-veg tag */}

        <View style={styles.imageWrapper}>
          <Image
            source={{ uri: displayInfo?.imageUrl || DEFAULT_ITEM_IMAGE_URL }}
            style={styles.image}
            resizeMode="cover"
          />

          {!!displayInfo?.tagIconUrl && (
            <Image source={{ uri: displayInfo.tagIconUrl }} style={styles.tagIcon} />
          )}
        </View>

        {/* Description */}

        <View style={styles.desc}>
          <Text style={styles.name} numberOfLines={2}>
            {getItemName(item)}
          </Text>

          {optionsText && <Text style={styles.options}>{optionsText}</Text>}

          {!!item?.note && (
            <View style={styles.noteRow}>
              <Image source={{ uri: ITEM_NOTE_ICON_URL }} style={styles.noteIcon} />

              <Text style={styles.noteText}>{item.note}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Quantity */}

      <Text style={styles.qty}>{String(item?.quantity ?? "")}</Text>

      {/* Amount */}

      <Text style={styles.amount} numberOfLines={1}>
        {money(item?.itemSubTotal)}
      </Text>
    </View>
  );
});

const OrderSummary = memo(function OrderSummary({
  money,
  summaryRows,
  billTotal,
}: {
  money: MoneyFormat;
  summaryRows: BillBreakdownRow[];
  billTotal: number;
}) {
  return (
    <View style={styles.summary}>
      {summaryRows.map((row) => (
        <View style={styles.row} key={row.label}>
          <Text style={styles.rowLabel}>{row.label}</Text>

          <Text style={styles.rowLabel}>{money(row.amount)}</Text>
        </View>
      ))}

      {/* Grand total */}

      <View style={[styles.row, styles.totalRow]}>
        <Text style={styles.totalText}>GRAND TOTAL</Text>

        <Text style={styles.totalText}>{money(billTotal)}</Text>
      </View>
    </View>
  );
});


const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#FFFFFF",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    paddingHorizontal: 32,
    paddingVertical: 20,

    borderBottomWidth: 1,
    borderBottomColor: "#E3E6ED",

    flexShrink: 0,
  },

  headerTitle: {
    color: "#12141A",
    fontSize: 24,
    fontWeight: "600",
  },

  headerSubtitle: {
    color: "#6A7085",
    fontSize: 16,
    fontWeight: "400",
  },

  customerChip: {
    flexDirection: "row",
    alignItems: "center",

    paddingVertical: 8,
    paddingHorizontal: 14,

    borderRadius: 24,
    borderWidth: 1,
    borderColor: "#E3E6ED",

    backgroundColor: "#F5F6FA",
  },

  avatarCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,

    alignItems: "center",
    justifyContent: "center",

    backgroundColor: "#E3E9FF",
    marginRight: 10,
  },

  avatarGlyph: {
    fontSize: 16,
  },

  customerChipText: {
    color: "#12141A",
    fontSize: 15,
    fontWeight: "600",
  },

  customerName: {
    color: "#12141A",
    fontSize: 15,
    fontWeight: "600",
  },

  customerPhone: {
    color: "#6A7085",
    fontSize: 13,
    marginTop: 2,
  },

  columnHeaderRow: {
    flexDirection: "row",
    alignItems: "center",

    paddingHorizontal: 32,
    paddingTop: 16,
    paddingBottom: 8,

    flexShrink: 0,
  },

  columnHeaderText: {
    color: "#9AA0B0",
    fontSize: 13,
    fontWeight: "600",
    letterSpacing: 0.5,
    marginRight: 14,
  },

  columnItemsHeader: {
    flex: 1,
  },

  columnQtyHeader: {
    width: 56,
    textAlign: "center",
  },

  columnPriceHeader: {
    width: 110,
    textAlign: "right",
  },

  itemsWrapper: {
    flex: 1,
    minHeight: 0,
  },

  items: {
    flex: 1,
  },

  itemsContent: {
    paddingHorizontal: 32,
    paddingVertical: 8,
  },

  item: {
    flexDirection: "row",
    alignItems: "center",

    padding: 16,
    marginBottom: 12,

    borderWidth: 1,
    borderColor: "#E3E6ED",
    borderRadius: 12,
  },

  itemMain: {
    flex: 1,
    flexDirection: "row",
    alignItems: "flex-start",
  },

  imageWrapper: {
    width: 64,
    height: 64,
    marginRight: 16,
    flexShrink: 0,
  },

  image: {
    width: "100%",
    height: "100%",
    borderRadius: 8,
    backgroundColor: "#F5F6FA",
  },

  tagIcon: {
    position: "absolute",
    top: 5,
    left: 5,
    width: 15,
    height: 15,
  },

  qty: {
    color: "#6A7085",
    fontSize: 20,
    fontWeight: "600",
    width: 56,
    textAlign: "center",
    alignSelf: "center",
  },

  desc: {
    flex: 1,
    minWidth: 0,
    paddingRight: 16,
  },

  name: {
    color: "#12141A",
    fontSize: 20,
    lineHeight: 28,
    fontWeight: "500",
  },

  options: {
    color: "#6A7085",
    fontSize: 15,
    lineHeight: 20,
    marginTop: 4,
  },

  noteRow: {
    flexDirection: "row",
    marginTop: 6,
  },

  noteIcon: {
    width: 14,
    height: 14,
    marginRight: 6,
  },

  noteText: {
    color: "#6078A3",
    fontSize: 14,
    fontWeight: "500",
  },

  amount: {
    color: "#12141A",
    fontSize: 20,
    fontWeight: "600",
    width: 110,
    textAlign: "right",
    alignSelf: "center",
  },

  summary: {
    paddingHorizontal: 32,
    paddingTop: 16,
    paddingBottom: 24,

    borderTopWidth: 1,
    borderTopColor: "#E3E6ED",

    backgroundColor: "#F5F6FA",

    flexShrink: 0,

    // Lifts the bill box above the scrolling item list behind it.
    shadowColor: "#12141A",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 8,
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    paddingVertical: 5,
  },

  rowLabel: {
    color: "#4A5064",
    fontSize: 18,
  },

  totalRow: {
    marginTop: 10,
    paddingTop: 14,

    borderTopWidth: 1,
    borderTopColor: "#E3E6ED",
  },

  totalText: {
    color: "#12141A",
    fontSize: 28,
    fontWeight: "700",
  },
});
